import { useCallback, useEffect, useState } from 'react';

const readLocalStorageValue = (key: string, defaultValue?: any) => {
  const evaluatedDefaultValue = typeof defaultValue === 'function' ? defaultValue() : defaultValue;
  if (typeof window === 'undefined') return evaluatedDefaultValue;

  const jsonValue = window.localStorage.getItem(key);
  if (jsonValue === null) return evaluatedDefaultValue;

  try {
    return JSON.parse(jsonValue);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (e) {
    return evaluatedDefaultValue;
  }
};

export const useLocalStorage = (key: string, defaultValue?: any) => {
  const [value, setValue] = useState(readLocalStorageValue(key, defaultValue));

  const setStoredValue = useCallback(
    (newValue: any) => {
      // accept a function so we have same API as useState
      const valueToStore = newValue instanceof Function ? newValue(value) : newValue;

      setValue(valueToStore);
      if (typeof window !== 'undefined') {
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
      }
    },
    [key, value]
  );

  useEffect(() => {
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === key) {
        setValue(readLocalStorageValue(key));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [key, setValue]);

  return [value, setStoredValue];
};
